import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * Gradient utilities (P2.1-S2 / Doc 30).
 *
 * - `<GradientText>` — text với violet→cyan gradient fill
 * - `<GradientBorder>` — div với gradient border via padding+bg trick
 * - `<BrandLogo>` — animated AI orb SVG cho login hero
 */

interface GradientTextProps extends React.HTMLAttributes<HTMLSpanElement> {
  as?: "span" | "h1" | "h2" | "h3" | "h4";
}

export function GradientText({
  as: Comp = "span",
  className,
  children,
  ...rest
}: GradientTextProps): JSX.Element {
  return (
    <Comp className={cn("text-gradient font-semibold", className)} {...rest}>
      {children}
    </Comp>
  );
}

interface GradientBorderProps extends React.HTMLAttributes<HTMLDivElement> {
  borderWidth?: number;
  rounded?: string;
}

/**
 * Wrapper với gradient border (1-2px). Dùng padding + background trick:
 * outer div có gradient bg, inner solid bg.
 */
export function GradientBorder({
  borderWidth = 1,
  rounded = "rounded-lg",
  className,
  children,
  ...rest
}: GradientBorderProps): JSX.Element {
  return (
    <div
      className={cn("bg-gradient-brand", rounded, className)}
      style={{ padding: borderWidth }}
      {...rest}
    >
      <div className={cn("bg-background", rounded)}>{children}</div>
    </div>
  );
}

/**
 * BrandLogo — AI orb SVG với gradient fill + slow rotate.
 * Dùng trong LoginPage hero.
 */
export function BrandLogo({
  size = 96,
  className,
}: {
  size?: number;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={cn("relative inline-flex items-center justify-center", className)}
      style={{ width: size, height: size }}
      aria-hidden
    >
      {/* Outer glow ring */}
      <span
        className="absolute inset-0 rounded-full bg-gradient-brand opacity-30 blur-2xl"
        aria-hidden
      />
      {/* Rotating gradient ring */}
      <svg
        viewBox="0 0 100 100"
        className="absolute inset-0 animate-spin-slow"
        style={{ width: size, height: size }}
      >
        <defs>
          <linearGradient id="brand-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="hsl(var(--brand-from))" />
            <stop offset="50%" stopColor="hsl(var(--brand-via))" />
            <stop offset="100%" stopColor="hsl(var(--brand-to))" />
          </linearGradient>
        </defs>
        <circle
          cx="50"
          cy="50"
          r="46"
          fill="none"
          stroke="url(#brand-grad)"
          strokeWidth="3"
          strokeDasharray="60 30"
          strokeLinecap="round"
        />
      </svg>
      {/* Core orb */}
      <span
        className="block size-1/2 rounded-full bg-gradient-brand shadow-glow animate-pulse-glow"
        aria-hidden
      />
    </div>
  );
}
